import * as React from "react";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { EMAIL_CATEGORIES, ORG_POSTAL, isCategoryOn } from "@/lib/email-preferences";
import {
  getEmailPreferences,
  updateEmailPreferences,
} from "@/lib/email-preferences.functions";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export const Route = createFileRoute("/email-preferences")({
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : "",
    action: typeof search.action === "string" ? search.action : "",
  }),
  head: () => ({
    meta: [
      { title: "Email preferences | T1D Warriors" },
      {
        name: "description",
        content:
          "Choose which emails you get from T1D Warriors: volunteer invitations, event news, program updates, or round-ups.",
      },
      { name: "robots", content: "noindex" },
      { property: "og:title", content: "Email preferences | T1D Warriors" },
      {
        property: "og:description",
        content: "Pick the T1D Warriors emails you want. You stay in control.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: EmailPreferencesPage,
});

function EmailPreferencesPage() {
  const { token, action } = useSearch({ from: "/email-preferences" });
  const load = useServerFn(getEmailPreferences);
  const save = useServerFn(updateEmailPreferences);

  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [email, setEmail] = React.useState<string | null>(null);
  const [categories, setCategories] = React.useState<Record<string, boolean>>({});
  const [allOff, setAllOff] = React.useState(false);
  const [notFound, setNotFound] = React.useState(false);

  React.useEffect(() => {
    let active = true;
    if (!token) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    load({ data: { token } })
      .then((result) => {
        if (!active) return;
        if (!result.found) {
          setNotFound(true);
        } else {
          setEmail(result.email);
          setCategories(result.categories);
          setAllOff(result.unsubscribedAll);
        }
      })
      .catch(() => active && setNotFound(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token, load]);

  const persist = async (next: Record<string, boolean>, unsubscribedAll: boolean) => {
    setSaving(true);
    try {
      const result = await save({ data: { token, categories: next, unsubscribedAll } });
      if (result.found) {
        setCategories(result.categories);
        setAllOff(result.unsubscribedAll);
        toast.success("Saved. Thank you for telling us.");
      }
    } catch {
      toast.error("We could not save that. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // A one-click unsubscribe link lands here and opts out immediately, but the
  // recipient can still turn individual categories back on.
  const autoRan = React.useRef(false);
  React.useEffect(() => {
    if (autoRan.current || loading || notFound || action !== "unsubscribe" || allOff) return;
    autoRan.current = true;
    void persist({}, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, notFound, action, allOff]);

  const toggle = (key: string, value: boolean) => {
    const next = { ...categories, [key]: value };
    setCategories(next);
    void persist(next, value ? false : allOff);
  };

  return (
    <main className="app-light min-h-screen bg-[#F1F9FC] px-5 py-14">
      <div className="mx-auto w-full max-w-xl rounded-md border border-[#d6e7f0] bg-white p-7">
        <h1 className="text-xl font-semibold text-[#0a1f2e]">Your email preferences</h1>

        {loading ? (
          <p className="mt-3 text-sm text-[#2c4a5c]">Loading your preferences...</p>
        ) : notFound ? (
          <p className="mt-3 text-sm text-[#2c4a5c]">
            That link is not valid or has expired. Email{" "}
            <a className="text-[#199CD6] underline" href={`mailto:${ORG_POSTAL.contactEmail}`}>
              {ORG_POSTAL.contactEmail}
            </a>{" "}
            and we will take care of it by hand.
          </p>
        ) : (
          <>
            <p className="mt-2 text-sm text-[#2c4a5c]">
              Choices for <span className="font-medium">{email}</span>. Turn off only what you do
              not want. Nothing else changes.
            </p>

            {allOff ? (
              <div className="mt-4 rounded border border-[#d6e7f0] bg-[#F1F9FC] p-3 text-sm text-[#2c4a5c]">
                You are opted out of everything except account and confirmation email. Turn any
                category back on below whenever you want.
              </div>
            ) : null}

            <ul className="mt-5 space-y-4">
              {EMAIL_CATEGORIES.map((category) => {
                const on = category.required
                  ? true
                  : !allOff && isCategoryOn(categories, category.key);
                return (
                  <li key={category.key} className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-[#0a1f2e]">{category.label}</p>
                      <p className="text-xs leading-relaxed text-[#5a7386]">
                        {category.description}
                      </p>
                    </div>
                    <Switch
                      checked={on}
                      disabled={category.required || saving}
                      aria-label={category.label}
                      onCheckedChange={(value) => toggle(category.key, value)}
                    />
                  </li>
                );
              })}
            </ul>

            {!allOff ? (
              <Button
                type="button"
                variant="outline"
                className="mt-6"
                disabled={saving}
                onClick={() => void persist({}, true)}
              >
                Unsubscribe from all non-essential email
              </Button>
            ) : null}
          </>
        )}

        <p className="mt-8 border-t border-[#e6eff5] pt-4 text-xs leading-relaxed text-[#7b8f9c]">
          {ORG_POSTAL.name}, a 501(c)(3) nonprofit. {ORG_POSTAL.line1}, {ORG_POSTAL.line2}.
          Questions? <a className="underline" href={`mailto:${ORG_POSTAL.contactEmail}`}>{ORG_POSTAL.contactEmail}</a>
        </p>
      </div>
    </main>
  );
}
